import React, { useState } from 'react';
import { authFetch, API } from '../api';
import { InboundFile } from './inboundApi';

// The supplier's invoice (and other files) on an inbound (Fernando 31.10: Henrik uploads the invoice to WGR when he
// pays it — the WMS holds it too, so the quantities can be checked later). Everyone downloads; a person with the
// inbound right (or any manager) attaches; a manager or the uploader removes. Read-only on a P&P station. -- Claude

const ACCEPT = 'application/pdf,image/png,image/jpeg,text/plain,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function download(f: InboundFile, onErr: (m: string) => void) {
  authFetch(`${API}/inbound/files/${f.id}`).then(async r => {
    if (!r.ok) { onErr('The file could not be fetched'); return; }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = f.filename; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }).catch(() => onErr('The file could not be fetched'));
}

export default function InboundFiles({ inboundId, files, canWork, manager, me, onChange, onErr }: {
  inboundId: number | string; files: InboundFile[]; canWork: boolean; manager: boolean; me: string;
  onChange: (d: any) => void; onErr: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const kb = (n: number) => n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
  const add = (file: File, kind: 'invoice' | 'other') => {
    if (file.size > 15 * 1024 * 1024) { onErr('At most 15 MB per file'); return; }
    const rd = new FileReader();
    rd.onload = async () => {
      setBusy(true);
      try {
        const r = await authFetch(`${API}/inbound/${inboundId}/files`, { method: 'POST', body: JSON.stringify({
          filename: file.name, mime: file.type || 'application/octet-stream', data_b64: String(rd.result).split(',')[1] || '', kind }) });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) { onErr(typeof j.detail === 'string' ? j.detail : j.detail?.message || `HTTP ${r.status}`); return; }
        onChange(j);
      } finally { setBusy(false); }
    };
    rd.readAsDataURL(file);
  };
  const remove = async (f: InboundFile) => {
    if (!window.confirm(`Remove the file "${f.filename}"?`)) return;
    const r = await authFetch(`${API}/inbound/files/${f.id}`, { method: 'DELETE' });
    const j = await r.json().catch(() => ({} as any));
    if (!r.ok) { onErr(typeof j.detail === 'string' ? j.detail : `HTTP ${r.status}`); return; }
    onChange(j);
  };
  const hasInvoice = files.some(f => f.kind === 'invoice');
  if (files.length === 0 && !canWork) return null;
  return (
    <div className="mb-4 rounded-xl border border-[#d1d5db] bg-white px-3 py-2">
      <div className="text-xs uppercase tracking-wider text-[#5f6368] mb-1">Invoice & files</div>
      <div className="flex flex-wrap gap-2 items-center">
        {files.map(f => (
          <span key={f.id} className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${f.kind === 'invoice' ? 'bg-amber-50 border border-amber-200' : 'bg-[#f3f4f6]'}`}>
            <button onClick={() => download(f, onErr)} className="text-blue-700 hover:underline font-medium" title={`${kb(f.size)} · ${f.uploaded_by || ''} ${f.uploaded_at || ''}`}>
              {f.kind === 'invoice' ? '📄 ' : '⬇ '}{f.filename}</button>
            <span className="text-[#6b7280] text-xs">{kb(f.size)}{f.uploaded_by ? ` · ${f.uploaded_by}` : ''}</span>
            {(manager || f.uploaded_by === me) && canWork && <button onClick={() => remove(f)} className="text-[#9aa0a6] hover:text-red-700 text-xs" title="remove this file">✕</button>}
          </span>
        ))}
        {files.length === 0 && <span className="text-sm text-[#6b7280]">No invoice attached yet.</span>}
        {canWork && (
          <label className={`text-sm px-3 py-1.5 rounded-lg cursor-pointer ${hasInvoice ? 'bg-[#dfe3e8] hover:bg-[#d0d5db]' : 'bg-blue-600 hover:bg-blue-500 text-white'} ${busy ? 'opacity-50' : ''}`}>
            {busy ? 'Uploading…' : hasInvoice ? '+ Attach a file' : '+ Attach the invoice'}
            <input type="file" accept={ACCEPT} className="hidden" disabled={busy}
              onChange={e => { const f = e.target.files?.[0]; if (f) add(f, hasInvoice ? 'other' : 'invoice'); e.target.value = ''; }} />
          </label>
        )}
      </div>
    </div>
  );
}
